import { formatMoney, toMoney } from '@betterment/core';
import * as Yup from 'yup';

import { DeferralSettings } from './types';

interface Contribution {
  type: 'percent' | 'amount';
  value?: number;
}

interface ContributionGroup {
  traditional?: Contribution;
  roth?: Contribution;
}

const MAX_PERCENT = 100;
const PERCENT_MAX_ERROR = 'Percent must be less than 100%';
const TOTAL_PERCENT_MAX_ERROR = 'Total percent must be less than 100%';

function getRootValues(context: Yup.TestContext): DeferralSettings {
  if (context.options.context) {
    return context.options.context as DeferralSettings;
  }

  if (!context.from || context.from.length === 0) {
    throw new Error('Validation context missing');
  }

  for (let i = context.from.length - 1; i >= 0; i -= 1) {
    // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment
    const { value } = context.from[i];
    if (value && typeof value === 'object' && 'base' in value) {
      return value as DeferralSettings;
    }
  }

  return context.from[context.from.length - 1].value as DeferralSettings;
}

function formatCentsAsCurrency(cents: number): string {
  return formatMoney(toMoney(cents));
}

function validatePercentageTotal(
  traditional?: { type: string; value?: number },
  roth?: { type: string; value?: number }
): boolean {
  if (
    traditional?.type === 'percent' &&
    roth?.type === 'percent' &&
    traditional.value !== undefined &&
    roth.value !== undefined
  ) {
    return traditional.value + roth.value <= MAX_PERCENT;
  }
  return true;
}

function calculateTotalAmountContributions(values: DeferralSettings): number {
  let total = 0;

  if (values.base?.traditional?.type === 'amount' && values.base.traditional.value) {
    total += values.base.traditional.value;
  }
  if (values.base?.roth?.type === 'amount' && values.base.roth.value) {
    total += values.base.roth.value;
  }

  if (values.bonus?.traditional?.type === 'amount' && values.bonus.traditional.value) {
    total += values.bonus.traditional.value;
  }
  if (values.bonus?.roth?.type === 'amount' && values.bonus.roth.value) {
    total += values.bonus.roth.value;
  }

  return total;
}

const baseContributionSchema = Yup.object().shape({
  type: Yup.string().oneOf(['percent', 'amount']).required(),
  value: Yup.number().when('type', ([type], schema: Yup.NumberSchema) => {
    return type === 'percent' ? schema.max(MAX_PERCENT, PERCENT_MAX_ERROR) : schema;
  }),
  legalAccountId: Yup.string().required(),
});

function createContributionSchemaWithLimit(contributionLimitCents: number) {
  return Yup.object().shape({
    type: Yup.string().oneOf(['percent', 'amount']).required(),
    value: Yup.number()
      .when('type', ([type], schema: Yup.NumberSchema) => {
        return type === 'percent' ? schema.max(MAX_PERCENT, PERCENT_MAX_ERROR) : schema;
      })
      .test('amount-limit', function checkAmountLimit(value) {
        // eslint-disable-next-line @typescript-eslint/no-unsafe-member-access
        if (this.parent.type !== 'amount') return true;

        if (!value || value === 0) return true;

        const rootValues = getRootValues(this);
        const total = calculateTotalAmountContributions(rootValues);

        if (total > contributionLimitCents) {
          return this.createError({
            message: `Total amount must be ${formatCentsAsCurrency(contributionLimitCents)} or less`,
          });
        }
        return true;
      }),
    legalAccountId: Yup.string().required(),
  });
}

function createAccountGroupSchema(contributionSchema: Yup.AnyObjectSchema, testName: string) {
  return Yup.object({
    traditional: contributionSchema.required(),
    roth: contributionSchema.notRequired().default(undefined),
  })
    .required()
    .test(testName, TOTAL_PERCENT_MAX_ERROR, (contributions: ContributionGroup) => {
      const { traditional, roth } = contributions;
      return validatePercentageTotal(traditional, roth);
    });
}

function createBonusSchema(contributionSchema: Yup.AnyObjectSchema) {
  return Yup.lazy(value =>
    value == null
      ? Yup.mixed().notRequired()
      : Yup.object({
          traditional: contributionSchema.notRequired(),
          roth: contributionSchema.notRequired(),
        }).test('bonus-total-percent', TOTAL_PERCENT_MAX_ERROR, (contributions: ContributionGroup) => {
          const { traditional, roth } = contributions;
          return validatePercentageTotal(traditional, roth);
        })
  );
}

export const createDeferralValidationSchema = (contributionLimitCents: number): Yup.ObjectSchema<DeferralSettings> => {
  const contributionSchemaWithLimit = createContributionSchemaWithLimit(contributionLimitCents);

  return Yup.object({
    base: createAccountGroupSchema(contributionSchemaWithLimit, 'base-total-percent'),
    bonus: createBonusSchema(contributionSchemaWithLimit),
  }) as Yup.ObjectSchema<DeferralSettings>;
};

export const validationSchema = Yup.object({
  base: createAccountGroupSchema(baseContributionSchema, 'base-total-percent'),
  bonus: createBonusSchema(baseContributionSchema),
});
